import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Tab, Tabs, Typography } from '@material-ui/core';
import PictureAsPdfOutlinedIcon from '@material-ui/icons/PictureAsPdfOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import moment from 'moment';

import * as actions from '../../../store/actions/index';
import Spinner from '../../UI/Spinner/Spinner';
import { EstadoComprobanteChip } from '../EstadoComprobanteTag/EstadoComprobanteTag';
import UltimaAccionImpresion from '../UltimaAccionImpresion/UltimaAccionImpresion';
import RenglonesComprobanteTable from '../RenglonesComprobanteTable/RenglonesComprobanteTable';
import CircuitoComprobante from '../CircuitoComprobante/CircuitoComprobante';
import HistorialComprobante from '../HistorialComprobante/HistorialComprobante';
import VisorPdf from '../VisorPdf/VisorPdf';
import { MailComprobanteCampos, MailComprobanteAcciones } from '../MailComprobanteForm/MailComprobanteForm';
import ImpresionWhatsappModal from '../ImpresionWhatsappModal/ImpresionWhatsappModal';
import useDatosComprobante, { claveComprobante } from '../hooks/useDatosComprobante';
import usePdfComprobante from '../hooks/usePdfComprobante';
import useMailComprobante from '../hooks/useMailComprobante';
import { formatNumberWithMonedaSigno } from '../../../assets/shared/formatNumber';
import { mismosDatos } from '../../../assets/shared/utility';
import { texto, tipoComprobante, numeroComprobante } from '../textosComprobante';
import colores from './coloresBandeja';
import { botonWhatsapp } from '../coloresWhatsapp';

const useStyles = makeStyles((theme) => ({
  panel: {
    backgroundColor: 'white',
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px',
    boxShadow: colores.sombra,
    overflowY: 'auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  volver: {
    alignSelf: 'flex-start',
    margin: '8px 8px 0',
    color: colores.secundario,
    textTransform: 'none'
  },
  cabecera: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '20px',
    padding: '20px 24px 0',
    [theme.breakpoints.down('xs')]: {
      gap: '12px',
      padding: '16px 16px 0'
    }
  },
  info: {
    flex: 1,
    minWidth: '260px',
    [theme.breakpoints.down('xs')]: {
      flexBasis: '100%',
      minWidth: 0
    }
  },
  tipo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: colores.secundario,
    textTransform: 'uppercase',
    letterSpacing: '0.06em'
  },
  cliente: {
    margin: '6px 0 4px',
    fontWeight: 500,
    fontSize: '22px',
    [theme.breakpoints.down('xs')]: {
      fontSize: '18px'
    }
  },
  meta: {
    color: colores.secundario,
    fontSize: '13px'
  },
  monto: {
    textAlign: 'right',
    '& b': {
      display: 'block',
      fontSize: '28px',
      fontWeight: 300
    },
    [theme.breakpoints.down('xs')]: {
      textAlign: 'left',
      '& b': {
        fontSize: '22px'
      }
    }
  },
  ultimaAccion: {
    fontSize: '12px',
    color: colores.secundario,
    marginTop: '4px'
  },
  botonWhatsapp,
  acciones: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    padding: '16px 24px',
    [theme.breakpoints.down('xs')]: {
      padding: '12px 16px',
      '& > *': {
        flex: '1 1 auto'
      },
      '& > span > button': {
        width: '100%'
      }
    }
  },
  tabs: {
    borderBottom: `1px solid ${colores.linea}`,
    padding: '0 12px',
    '& .MuiTab-root': {
      minWidth: 0,
      padding: '6px 14px'
    },
    '& .Mui-selected': {
      color: colores.naranja
    },
    '& .MuiTabs-indicator': {
      height: '3px',
      background: colores.gradiente
    }
  },
  cuerpo: {
    padding: '18px 24px',
    flex: 1,
    [theme.breakpoints.down('xs')]: {
      padding: '12px 16px'
    }
  },
  preview: {
    height: '70vh',
    [theme.breakpoints.down('xs')]: {
      height: '60vh'
    },
    backgroundColor: '#ececec',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
  },
  mail: {
    maxWidth: '720px'
  },
  pieMail: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: '10px'
  },
  vacio: {
    padding: '40px',
    textAlign: 'center',
    color: colores.tenue
  }
}));

const pestanias = [
  { clave: 'renglones', nombre: 'Renglones' },
  { clave: 'descargas', nombre: 'Descargas' },
  { clave: 'pdf', nombre: 'Vista previa' },
  { clave: 'historial', nombre: 'Historial' }
];

/** Facturas del circuito cuyo saldo se compara en el refresco: "idSucursal|idSistema|idComproba|nroReferencia". */
const facturasDelCircuito = (circuito) =>
  circuito
    ? circuito.nodos
        .filter((nodo) => nodo.remunerativo && !nodo.esRecibo && !nodo.anulado)
        .map((nodo) => `${nodo.idSucursal}|${nodo.idSistema}|${nodo.idComproba}|${nodo.nroReferencia}`)
    : [];

const mismasClaves = (a, b) => mismosDatos(a.map((saldo) => saldo.clave), b.map((saldo) => saldo.clave));

/**
 * Si entre dos respuestas de renglones cambió algo que obliga a volver a pedir lo demás: los renglones (pendientes,
 * totales) o el saldo de alguna factura. Los saldos sólo se comparan si son de las mismas facturas (la primera vez
 * se piden sin circuito y vienen vacíos).
 */
const cambioElDetalle = (anterior, actual) =>
  !mismosDatos(anterior.renglones, actual.renglones) ||
  (mismasClaves(anterior.saldosFacturas || [], actual.saldosFacturas || []) && !mismosDatos(anterior.saldosFacturas, actual.saldosFacturas));

/** Baja el archivo del blob URL como si se hubiera hecho clic en un link de descarga. */
const bajarArchivo = (pdf) => {
  const link = document.createElement('a');
  link.href = pdf.url;
  link.download = pdf.nombreArchivo;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Detalle del comprobante elegido en la bandeja: cabecera, acciones (PDF, mail, WhatsApp) y pestañas con los
 * renglones (y lo cobrado), el circuito de descargas, la vista previa del PDF y el historial de envíos.
 * El mail se escribe en el mismo panel. La vista previa no registra la descarga; "Descargar PDF" sí.
 * Con onVolver (pantallas chicas, donde el detalle reemplaza a la lista) se muestra "Volver a la lista".
 */
const BandejaDetalle = ({ comprobante, refresco, mailAbierto, onAbrirMail, onCerrarMail, onVolver }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [pestania, setPestania] = useState(pestanias[0].clave);
  const [descargaPedida, setDescargaPedida] = useState(false);
  const [whatsappAbierto, setWhatsappAbierto] = useState(false);
  const [versionDetalle, setVersionDetalle] = useState(0);
  const clave = claveComprobante(comprobante);

  // Refresco: el contador que había al elegir el comprobante. Desde el siguiente se piden los renglones aunque
  // se esté en otra pestaña, para saber si hay que actualizar lo demás.
  const elegido = useRef({ clave: null, refresco });
  if (elegido.current.clave !== clave) elegido.current = { clave, refresco };
  const refrescado = refresco !== elegido.current.refresco;

  // Cada pestaña pide lo suyo. En Renglones primero llegan los renglones y después el circuito, que da el importe
  // pago; el circuito queda para Descargas. Los renglones del refresco traen también el saldo de las facturas del
  // circuito, porque un pago nuevo no cambia los renglones.
  const facturas = useRef([]);
  const detalle = useDatosComprobante(
    comprobante,
    (comp, success, fail) => actions.loadRenglonesComprobanteImpresion(comp, success, fail, { facturas: facturas.current }),
    { activo: pestania === 'renglones' || refrescado, version: refresco }
  );
  const circuito = useDatosComprobante(comprobante, actions.loadCircuitoComprobanteImpresion, {
    activo: pestania === 'descargas' || (pestania === 'renglones' && !!detalle.datos),
    version: versionDetalle
  });
  facturas.current = facturasDelCircuito(circuito.datos);
  const historial = useDatosComprobante(comprobante, actions.loadHistorialComprobanteImpresion, {
    activo: pestania === 'historial',
    version: comprobante && comprobante.ultimaAccion ? comprobante.ultimaAccion.fecha : null
  });
  const { pdf, error: errorPdf } = usePdfComprobante(comprobante, {
    registrar: false,
    activo: pestania === 'pdf' || descargaPedida,
    version: versionDetalle
  });

  // Si en un refresco cambiaron los renglones o el saldo de alguna factura, se vuelve a pedir lo demás.
  const renglonesAnteriores = useRef({ clave: null, datos: null });
  useEffect(() => {
    const anterior = renglonesAnteriores.current;
    if (detalle.datos && anterior.clave === clave && anterior.datos && cambioElDetalle(anterior.datos, detalle.datos)) {
      setVersionDetalle((version) => version + 1);
    }
    if (detalle.datos) renglonesAnteriores.current = { clave, datos: detalle.datos };
  }, [detalle.datos, clave]);
  const mail = useMailComprobante(mailAbierto ? comprobante : null, onCerrarMail);

  useEffect(() => setDescargaPedida(false), [clave]);

  // "Descargar PDF" espera a que el PDF esté generado, lo baja y registra la descarga.
  useEffect(() => {
    if (!descargaPedida) return;
    if (errorPdf) {
      setDescargaPedida(false);
    } else if (pdf) {
      bajarArchivo(pdf);
      dispatch(actions.registrarDescargaImpresion(comprobante));
      setDescargaPedida(false);
    }
  }, [descargaPedida, pdf, errorPdf, comprobante, dispatch]);

  if (!comprobante) {
    return <div className={`${classes.panel} ${classes.vacio}`}>Elegí un comprobante de la lista</div>;
  }

  const anulado = comprobante.estado === 'Anulado';

  const contenidoPestania = () => {
    switch (pestania) {
      case 'renglones':
        if (detalle.error) return <Typography color="error">{detalle.error}</Typography>;
        if (!detalle.datos) return <Spinner size="small" />;
        return (
          <RenglonesComprobanteTable
            renglones={detalle.datos.renglones}
            importePago={circuito.datos ? circuito.datos.importePago : null}
            errorImportePago={circuito.error}
          />
        );
      case 'descargas':
        return <CircuitoComprobante raiz={comprobante} circuito={circuito.datos} error={circuito.error} />;
      case 'pdf':
        return (
          <div className={classes.preview}>
            <VisorPdf pdf={pdf} error={errorPdf} />
          </div>
        );
      default:
        return <HistorialComprobante acciones={historial.datos} error={historial.error} />;
    }
  };

  return (
    <div className={classes.panel}>
      {onVolver && (
        <Button className={classes.volver} size="small" startIcon={<ArrowBackIcon />} onClick={onVolver}>
          Volver a la lista
        </Button>
      )}
      <div className={classes.cabecera}>
        <div className={classes.info}>
          <div className={classes.tipo}>
            {`${tipoComprobante(comprobante)} · Ref. ${comprobante.nroReferencia}`}
            <EstadoComprobanteChip estado={comprobante.estado} style={{ height: '20px' }} />
          </div>
          <div className={classes.cliente}>{texto(comprobante.nombre)}</div>
          <div className={classes.meta}>
            {`${texto(comprobante.idCliente)} · N° ${numeroComprobante(comprobante)} · ${moment(comprobante.fecha).format('DD/MM/YYYY HH:mm')}`}
          </div>
        </div>
        <div className={classes.monto}>
          <b>{formatNumberWithMonedaSigno(comprobante.total)}</b>
          <div className={classes.ultimaAccion}>
            <UltimaAccionImpresion ultimaAccion={comprobante.ultimaAccion} />
          </div>
        </div>
      </div>

      <div className={classes.acciones}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={descargaPedida ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfOutlinedIcon />}
          disabled={descargaPedida}
          onClick={() => setDescargaPedida(true)}
        >
          Descargar PDF
        </Button>
        <Button variant="contained" color="primary" startIcon={<MailOutlineIcon />} disabled={anulado || mailAbierto} onClick={onAbrirMail}>
          Enviar por mail
        </Button>
        <Button
          variant="contained"
          className={classes.botonWhatsapp}
          startIcon={<WhatsAppIcon />}
          disabled={anulado}
          onClick={() => setWhatsappAbierto(true)}
        >
          WhatsApp
        </Button>
      </div>
      <ImpresionWhatsappModal comprobante={whatsappAbierto ? comprobante : null} onClose={() => setWhatsappAbierto(false)} />

      {mailAbierto ? (
        <div className={classes.cuerpo}>
          <div className={classes.mail}>
            <MailComprobanteCampos mail={mail} />
            <div className={classes.pieMail}>
              <MailComprobanteAcciones mail={mail} onCancelar={onCerrarMail} />
            </div>
          </div>
        </div>
      ) : (
        <React.Fragment>
          <Tabs
            className={classes.tabs}
            value={pestania}
            onChange={(event, valor) => setPestania(valor)}
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {pestanias.map((item) => (
              <Tab key={item.clave} value={item.clave} label={item.nombre} />
            ))}
          </Tabs>
          <div className={classes.cuerpo}>{contenidoPestania()}</div>
        </React.Fragment>
      )}
    </div>
  );
};

export default BandejaDetalle;
